import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  CheckBox,
  Heading,
  Spinner,
  Text,
} from "grommet";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import API_URL from "../../config/config";
import { HeaderNavigation } from "../../components/Headers/GlobalHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import { LinkPrevious } from "grommet-icons";
import { useSequence } from "../../components/SequenceProvider";
import BreadcrumbNavigation from "../../components/ManagerPortal/BreadcrumbNavigation2";
import { SendInvite } from "../../components/ManagerPortal/SendInvite";

export const SelectUsersGroups = () => {
  const navigate = useNavigate();
  const { manager_id, assessment_id } = useParams();

  // MAIN STATE
  const [users, setUsers] = useState([]);

  // SELECTION STATES
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // LOADING & ERROR
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [error, setError] = useState("");
  const { isSequenceCompleted, startSequence } = useSequence();
  const [showInviteForm, setShowInviteForm] = useState(false);
  useEffect(() => {
    
    if (isSequenceCompleted) {
      // Redirect to dashboard if sequence is completed
      navigate(`/${manager_id}/manager/assessments`, { replace: true });
    } else {
      // Start the sequence
      startSequence();
    }
  }, [isSequenceCompleted, navigate, startSequence]);

  useEffect(() => {
    fetchUsersAndGroups();
  }, []);

  // Fetch users, userIds (mappings), and groups for the manager
  const fetchUsersAndGroups = async () => {
    try {
      // Fetch all users (and userIds) from get_all_users
      const userResponse = await axios.get(
        `${API_URL}/manager/get_invited_users_status/${manager_id}`,
      );

      const fetchedUsers = userResponse.data.invites || [];
      // const fetchedUserIds = userResponse.data.userIds || [];

      setUsers(fetchedUsers);
      setIsLoadingUsers(false);
    } catch (err) {
      console.error("Error fetching users and groups:", err);
      setError("Failed to fetch users and groups. Please try again.");
      setIsLoadingUsers(false);
    }
  };

  // Toggle an individual user
  const handleUserSelection = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId],
    );
  };

  // Filter users with `user_status === 1`
  // const validUsers = users.filter((user) => user.invite_status === 3);
  const acceptedUsers = users.filter((user) => user.invite_status !== 2);
  // Handle form submission
  const handleSubmit = async () => {
    // if (selectedUsers.length === 0) {
    //   setError("Please select at least one user.");
    //   return;
    // }

    try {
      

      setSelectedUsers([]);
      setSelectedGroups([]);
      setError("");
      navigate(`/manager/${manager_id}/${assessment_id}/add-proficiency`, {
        state: { selectedUsers, selectedGroups },
      });
    } catch (error) {
      console.error("Error submitting selection:", error);
      setError("Failed to submit selection. Please try again.");
    }
  };
  return (
    <Box fill="vertical" justify="between">
      {/* Header */}
      <HeaderNavigation />

      {/* Main Body */}
      <Box flex="grow" pad="medium">
        {/* Back Button */}
        <Box
          margin={{ bottom: "medium" }}
          pad="small"
          direction="row"
          align="center"
          justify="between"
          // background="light-2"
          // round="small"
          // elevation="small"
          height="50px"
        >
          {/* Left side: Back Arrow + Title */}
          <Box direction="row" align="center" gap="small">
            <LinkPrevious
              size="large"
              onClick={() => navigate(-1)}
              color="brand"
            />
            <BreadcrumbNavigation
              current={"Step 1: Select Users"}
              previous={"Manage Assessments"}
              // current_link={`/manager/${manager_id}/${assessment_id}/select-users`}
              // previous_link={`/${manager_id}/manager/assessments`}
            />
          </Box>
        </Box>
        {/* Error Message */}
        {error && (
          <Box background="status-critical" pad="small" margin="small">
            <Text>{error}</Text>
          </Box>
        )}

        <Box
          pad="medium"
          gap="medium"
          background="light-1"
          round="small"
          overflow="auto"
        >
          {/* USERS TABLE */}
          <Box direction="row" justify="between" align="center">
            <Box direction="column">
              <Heading level="2">Team</Heading>
              <Text>
                This page allows you to add users to the assessment. Adding
                users is optional at this stage—they can be added later if
                needed. <br />
                Use the checkboxes to select participants and click Submit
                Selection to proceed.
              </Text>
            </Box>
            <Box>
              <SendInvite
                manager_id={manager_id}
                isOpen={showInviteForm} // Control visibility
                users={users.length}
                onClose={() => setShowInviteForm(false)} // Handle closing
                onUserAdded={() => {
                  window.location.reload();
                }}
              />
            </Box>
          </Box>
          <Table>
            <TableHeader>
              <TableRow>
                <TableCell scope="col">
                  <Text weight="bold">Status</Text>
                </TableCell>
                <TableCell scope="col">
                  <Text weight="bold">User</Text>
                </TableCell>
                <TableCell scope="col">
                  <Text weight="bold">Invite status</Text>
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingUsers ? (
                <TableRow>
                  <TableCell colSpan="4">
                    <Box
                      direction="row"
                      justify="center"
                      align="center"
                      pad="small"
                    >
                      <Spinner />
                      <Text margin={{ left: "small" }}>Loading users...</Text>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : acceptedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="4">
                    No users have accepted your invite yet.
                  </TableCell>
                </TableRow>
              ) : (
                acceptedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <CheckBox
                        checked={selectedUsers.includes(user.user_id)}
                        onChange={() => handleUserSelection(user.user_id)}
                      />
                    </TableCell>
                    {/* <TableCell>{user.name || "Unnamed User"}</TableCell> */}
                    <TableCell>
                      {user.name}
                      <br />
                      {user.user_email}
                    </TableCell>
                    <TableCell>
                      {user.invite_status === 0
                        ? "Pending"
                        : user.invite_status === 1
                          ? "Accepted"
                          : user.invite_status === 2
                            ? "Declined"
                            : user.invite_status === 3
                              ? "Active"
                              : "Unknown"}
                    </TableCell>
                    {/* <TableCell>
                      {user.groupNames && user.groupNames.length
                        ? user.groupNames.join(", ")
                        : "No Groups"}
                    </TableCell> */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
        <Box align="center" justify="center" margin={{ top: "medium" }}>
          <Button
            label={
              selectedUsers.length > 0 ? "Submit selection" : "Skip for now"
            }
            onClick={handleSubmit}
            primary
          />
        </Box>
      </Box>
      {/* <Box direction="row" gap="medium" margin="medium" justify="center">
        <Button label="Submit selection" onClick={handleSubmit} primary />
      </Box>
      <StickyFooter /> */}

      <StickyFooter />
    </Box>
  );
};
